import {
  LinkActionResult,
  Maybe,
  OpenPdfUrlAction,
} from '@varo/graphql-schema-types';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

interface MultiStepTermsSectionProps extends PressableAnalyticsProps {
  actions: LinkActionResult[];
  heading: string;
  html: Maybe<string> | undefined;
  handlePdfClick: (url: string) => void;
}

const MultiStepTermsSection = ({
  actions,
  heading,
  html,
  analyticsId = '',
  customAttributes = null,
  handlePdfClick,
}: MultiStepTermsSectionProps) => {
  const multiStepIncentiveTermsAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  function getHrefId(termsHtml: Maybe<string> | undefined) {
    const startIndex = termsHtml?.indexOf("href='");
    if (startIndex && startIndex !== -1) {
      const hrefStartIndex = startIndex + 6;

      const endIndex = termsHtml?.indexOf("'", hrefStartIndex);
      if (endIndex !== -1) {
        return termsHtml?.substring(hrefStartIndex, endIndex);
      }
    }
  }

  const hrefId = getHrefId(html);

  const action = actions?.find(
    (action) => action.id === hrefId,
  ) as OpenPdfUrlAction;

  function replaceHrefValue(
    termsHtml: Maybe<string> | undefined,
    newHref: string,
  ) {
    const startIndex = termsHtml?.indexOf("href='");
    if (startIndex && startIndex !== -1 && termsHtml) {
      const hrefStartIndex = startIndex + 6;
      const endIndex = termsHtml.indexOf("'", hrefStartIndex);
      if (endIndex !== -1) {
        const updatedTermsHtml =
          termsHtml.substring(0, hrefStartIndex) +
          newHref +
          termsHtml.substring(endIndex as number);

        return updatedTermsHtml;
      }
    }
  }

  const replacedHrefValue = replaceHrefValue(html, action?.url);

  const extractHyperlinkText = (html: string): string | null => {
    const regex: RegExp = /<a [^>]*>(.*?)<\/a>/;
    const match: RegExpMatchArray | null = html.match(regex);
    return match ? match[1] : null;
  };

  const handleLinkPress = () => {
    multiStepIncentiveTermsAnalytics();
    handlePdfClick(action.url);
  };

  const renderers = {
    a: () => {
      return (
        <HyperlinkXsText onPress={() => handleLinkPress()}>
          {extractHyperlinkText(replacedHrefValue ?? '')}
        </HyperlinkXsText>
      );
    },
  };

  return (
    <View style={styles.container}>
      <BodySmBoldText>{heading ?? ''}</BodySmBoldText>
      <HTMLRenderer
        htmlAsString={replaceHrefValue(html, action?.url) ?? ''}
        tagsStyles={htmlStyles}
        renderers={renderers}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
});

const htmlStyles = StyleSheet.create({
  // eslint-disable-next-line react-native/no-unused-styles
  p: {
    color: theme.color.text.secondary,
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.24,
    lineHeight: 16,
  },
});

export default MultiStepTermsSection;
